'use client';
import { Button } from '@curology/ui-components-web/react/Button';
import { Typography } from '@curology/ui-components-web/react/Typography';
import Image from 'next/image';
import { useEffect, useState } from 'react';

import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import { LiveConsultationService } from '@/services/liveConsultationService/client';
import {
  type ProviderData,
  type AvailableTimeSlots,
} from '@/services/liveConsultationService/types';
import { type OptionsType } from '@/services/liveConsultationService/utils';

import { useNavigation } from '../../../../hooks/useNavigation';
import Form from '../form';

const ResubscriptionConsultation = ({
  availableTimeSlots,
  dateOptions,
  timeOptions,
}: {
  availableTimeSlots: AvailableTimeSlots;
  dateOptions: OptionsType;
  timeOptions: OptionsType;
}) => {
  const navigation = useNavigation();
  const { brand } = useFlowContext();
  const [provider, setProvider] = useState<ProviderData>({
    name: '',
    profilePhotoUrl: null,
  });

  useEffect(() => {
    const getProviderInformation = async () => {
      const providerInformation =
        await LiveConsultationService.getProviderInformation(brand);
      setProvider(providerInformation.data.data);
    };
    getProviderInformation().catch(console.error);
  }, [brand]);

  const skipButton = (
    <Button
      type="button"
      buttonType="text"
      state="dark"
      className="cur-mt-4"
      onClick={navigation.onNext}
    >
      Skip
    </Button>
  );

  return (
    <div className="cur-flex cur-flex-col cur-space-y-12">
      <Typography size="h2" className="!cur-text-primary">
        Almost there! Set up a quick call with your provider
      </Typography>
      <div className="cur-flex cur-gap-8">
        {provider.profilePhotoUrl && (
          <div className="cur-min-w-[80px]">
            <Image
              src={provider.profilePhotoUrl}
              alt="Provider profile image"
              width={80}
              height={80}
            />
          </div>
        )}
        <Typography size="meta">
          {`If you’ve previously talked to ${provider.name} on the phone, you can skip this step.`}
          <br />
          <br />
          Otherwise, this call is required by telemedicine laws in your state.
          We encourage you to sign up for the earliest time so you can get
          started sooner.
        </Typography>
      </div>
      {dateOptions.length === 0 ? (
        <Typography size="body">
          All time slots are currently full. Please check again later.
        </Typography>
      ) : (
        <>
          <Typography as="p" size="body">
            Meet your provider over the phone to go over a few final details
            before they create your formula. This should only take a few minutes
            👌
          </Typography>
          <Form
            availableTimeSlots={availableTimeSlots}
            dateOptions={dateOptions}
            timeOptions={timeOptions}
            secondaryButton={skipButton}
          />
        </>
      )}
    </div>
  );
};

export default ResubscriptionConsultation;
